import { useData } from "@/context/DataContext";
import { KpiCard, PageHeader, Panel } from "@/components/Widgets";
import { ChartGrid } from "@/pages/ChartPages";
import { metricSeries, fmtU } from "@/lib/metrics";
import { cut, pct, sum, last } from "@/lib/api";

const cagr = (a) => (a.length > 1 && a[0] > 0 && last(a) > 0 ? (last(a) / a[0]) ** (1 / (a.length - 1)) - 1 : null);

const useKpis = () => {
  const { data, horizon: h, filters } = useData();
  const ent = filters.entity;
  const s = (k) => cut(metricSeries(data, k, ent), h);
  const years = cut(data.model.years, h);
  const rev = s("income"), opex = s("expenses"), eb = s("ebitda"), capex = s("capex"), intr = s("interest_expense");
  const ncf = s("ncf"), debt = s("debt"), rep = s("repayment");
  const closing = cut(data.group.closing, h);
  const minC = Math.min(...closing);
  const dscrY = eb.map((v, i) => (intr[i] + rep[i] > 0.01 ? v / (intr[i] + rep[i]) : null));
  const dscrVals = dscrY.filter((v) => v != null);
  const posYear = years.find((_, i) => ncf.slice(i).every((v) => v >= 0));
  const shares = data.model.entities.filter((e) => e.switch).map((e) => ({ e, r: sum(cut(e.series.income, h)), b: sum(cut(e.series.ebitda, h)) }));
  const R = sum(shares.map((x) => x.r)), B = sum(shares.filter((x) => x.b > 0).map((x) => x.b));
  const sorted = [...shares].sort((a, b) => b.r - a.r);
  const top2 = R ? (sorted.slice(0, 2).reduce((a, x) => a + x.r, 0) / R) : 0;
  const hhi = R ? shares.reduce((a, x) => a + (x.r / R) ** 2, 0) : 0;
  return { years, rev, opex, eb, capex, intr, ncf, debt, closing, minC, dscrVals, dscrY, posYear, shares, R, B, sorted, top2, hhi };
};

const ShareList = ({ title, rows, total, k, testid }) => (
  <Panel title={title} testid={testid}>
    <div className="space-y-2">
      {rows.map((x) => {
        const v = total ? (Math.max(0, x[k]) / total) * 100 : 0;
        return (
          <div key={x.e.code} className="text-xs">
            <div className="flex justify-between text-slate-300"><span>{x.e.short}</span><span className="num">{x[k] < 0 ? "negative" : `${v.toFixed(1)}%`}</span></div>
            <div className="mt-1 h-1.5 rounded bg-slate-800"><div className="h-1.5 rounded" style={{ width: `${v}%`, background: k === "r" ? "#3B82F6" : "#14B8A6" }} /></div>
          </div>
        );
      })}
    </div>
  </Panel>
);

export default function ExecutiveSummary() {
  const { data, filters } = useData();
  const k = useKpis();
  const fmtCr = (v) => (filters.unit === "lakh" ? fmtU(v * 100, "lakh") : fmtU(v, "cr"));
  const ent = data.model.entities.find((e) => e.code === filters.entity);
  const debtEb = last(k.eb) > 0 ? last(k.debt) / last(k.eb) : null;
  const avgDscr = k.dscrVals.length ? k.dscrVals.reduce((a, b) => a + b, 0) / k.dscrVals.length : null;
  const minDscr = k.dscrVals.length ? Math.min(...k.dscrVals) : null;
  const cards = [
    ["kpi-revenue", "Total revenue", fmtCr(sum(k.rev)), `FY ${k.years[0]} ${fmtCr(k.rev[0])}`, "blue"],
    ["kpi-opex", "Operating expenses", fmtCr(sum(k.opex)), `${pct(sum(k.opex) / sum(k.rev))} of revenue`, "neg"],
    ["kpi-ebitda", "EBITDA", fmtCr(sum(k.eb)), "Horizon total", "pos"],
    ["kpi-margin", "EBITDA margin", pct(sum(k.eb) / sum(k.rev)), `FY ${last(k.years)}: ${pct(last(k.eb) / last(k.rev))}`, "pos"],
    ["kpi-capex", "Total capex", fmtCr(sum(k.capex)), "Unit + project capex", "amber"],
    ["kpi-interest", "Interest expense", fmtCr(sum(k.intr)), "On unit loan schedules", "neg"],
    ["kpi-ncf", "Net cash flow", fmtCr(sum(k.ncf)), ent ? "Entity sheet net cash flow" : "Group NCF (after central items)", sum(k.ncf) >= 0 ? "pos" : "neg"],
    ["kpi-closing", `Closing cash FY ${last(k.years)}`, fmtCr(last(k.closing)), "Group (central) balance", last(k.closing) >= 0 ? "pos" : "neg"],
    ["kpi-debt", `Outstanding debt FY ${last(k.years)}`, fmtCr(last(k.debt)), ent ? ent.short : "Loans sheet grand total", "blue"],
    ["kpi-debt-ebitda", "Debt / EBITDA", debtEb != null ? `${debtEb.toFixed(2)}×` : "n/a", `FY ${last(k.years)} closing debt ÷ EBITDA`, "amber"],
    ["kpi-dscr", "Debt-service coverage", avgDscr != null ? `${avgDscr.toFixed(2)}×` : "n/a", minDscr != null ? `Avg; lowest ${minDscr.toFixed(2)}× · EBITDA ÷ (interest + repayment)` : "No debt service", minDscr != null && minDscr < 1.2 ? "neg" : "pos"],
    ["kpi-peak-funding", "Peak funding requirement", fmtCr(Math.max(0, -k.minC)), "Deepest negative group cash balance", k.minC < 0 ? "neg" : "pos"],
    ["kpi-lowest-cash", "Lowest cash balance", fmtCr(k.minC), `FY ${k.years[k.closing.indexOf(k.minC)]}`, k.minC < 0 ? "neg" : "pos"],
    ["kpi-revenue-cagr", "Revenue CAGR", cagr(k.rev) != null ? pct(cagr(k.rev)) : "n/a", `FY ${k.years[0]} – FY ${last(k.years)}`, "blue"],
    ["kpi-ebitda-cagr", "EBITDA CAGR", cagr(k.eb) != null ? pct(cagr(k.eb)) : "n/a (negative base)", `FY ${k.years[0]} – FY ${last(k.years)}`, "pos"],
    ["kpi-cf-positive-year", "Cash-flow-positive year", k.posYear ? `FY ${k.posYear}` : "Not within horizon", "First year NCF stays ≥ 0", k.posYear ? "pos" : "neg"],
    ["kpi-concentration", ent ? "Top-2 revenue concentration (group)" : "Top-2 revenue concentration", pct(k.top2), `${k.sorted.slice(0, 2).map((x) => x.e.short).join(" + ")} · HHI ${k.hhi.toFixed(2)}`, k.top2 > 0.6 ? "neg" : "amber"],
  ];
  return (
    <div data-testid="executive-summary-page">
      <PageHeader kicker="Executive Summary" title={ent ? `SRM Group at a glance - ${ent.short}` : "SRM Group at a glance"} />
      <div className="mb-6 flex flex-wrap gap-3" data-testid="active-model-banner">
        <span className="rounded-full bg-sky-500/10 px-3 py-1 text-xs text-sky-300 ring-1 ring-sky-500/30">Active version: <b data-testid="active-version-name">{data.version.name}</b></span>
        <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300 ring-1 ring-emerald-500/30" data-testid="active-scenario-name">Scenario: Base (workbook values)</span>
        <span className={`rounded-full px-3 py-1 text-xs ring-1 ${data.version.validation_ok ? "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30" : "bg-rose-500/10 text-rose-300 ring-rose-500/30"}`} data-testid="active-validation-status">
          Validation: {data.version.validation_ok ? "reconciled" : "differences found"}
        </span>
        {ent && <span className="rounded-full bg-amber-500/10 px-3 py-1 text-xs text-amber-300 ring-1 ring-amber-500/30">Filtered: {ent.name}</span>}
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {cards.map(([id, l, v, sub, tone], i) => <KpiCard key={id} testid={id} label={l} value={v} sub={sub} tone={tone} delay={i * 25} />)}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ShareList title={ent ? "Contribution to group revenue (all entities)" : "Contribution to group revenue"} rows={k.sorted} total={k.R} k="r" testid="revenue-contribution" />
        <ShareList title={ent ? "Contribution to group EBITDA (all entities)" : "Contribution to group EBITDA"} rows={[...k.shares].sort((a, b) => b.b - a.b)} total={k.B} k="b" testid="ebitda-contribution" />
      </div>
      <div className="mt-6">
        <ChartGrid keys={["revenueTrend", "revenueByEntity", "ebitdaMargin", "opexTrend", "ncfTrend", "cashBalances", "capexByEntity", "capexByProject", "debtFlows", "interest", "revenueMix", "ebitdaMix", "ghInorx", "actualVsProjected", "scenarios", "versionCompare"]} />
      </div>
    </div>
  );
}
